import { unwrapResult } from "@reduxjs/toolkit"
import CheckIcon from "@mui/icons-material/Check"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined"
import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import RefreshIcon from "@mui/icons-material/Refresh"
import {
    Alert,
    Box,
    Button,
    ButtonBase,
    CircularProgress,
    FormControl,
    InputLabel,
    Menu,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react"

import { useAppDispatch } from "../../../app/hooks"
import {
    DOCUMENT_CONTENT_ACTIONS,
    DOCUMENT_STATUSES,
    DocumentContentActions,
    DocumentStatus,
} from "../../documents/documentsApi"
import { DocumentStatusLabel } from "../../documents/DocumentStatusLabel"
import {
    loadDocumentContentActionRules,
    SysDocumentContentActionRule,
    SubjectType,
    upsertSysDocumentContentActionRule,
} from "./systemWorkflowApi"
import {
    normalizeActionCode,
    normalizeSubjectType,
    subjectTypeLabel,
    SYS_DOCUMENT_CONTENT_ACTION_RULE_SUBJECT_TYPES,
} from "./systemWorkflowHelpers"

const getCellKey = (documentStatus: string, subjectType: string) => `${documentStatus}__${subjectType}`
type EditMode = "single" | "column" | "row"

type DraftRuleChange = {
    documentStatus: DocumentStatus
    subjectType: SubjectType
    enabled: boolean
}

const EDIT_MODES: { value: EditMode; label: string }[] = [
    { value: "single", label: "Pojedyncza reguła" },
    { value: "column", label: "Według kolumny" },
    { value: "row", label: "Według wiersza" },
]

const SELECTION_OPACITY = { axis: 0.08, cell: 0.18 }
const PANEL_BOTTOM_GAP = 50

export const SysDocumentContentActionByActionView = () => {
    const dispatch = useAppDispatch()
    const componentId = useId()
    const actionLabelId = `${componentId}-action-filter-label`
    const modeButtonId = `${componentId}-mode-button`
    const modeMenuId = `${componentId}-mode-menu`
    const panelRef = useRef<HTMLDivElement | null>(null)
    const [panelHeight, setPanelHeight] = useState<number>()

    const [rules, setRules] = useState<SysDocumentContentActionRule[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [hasLoaded, setHasLoaded] = useState(false)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [saveError, setSaveError] = useState<string | null>(null)
    const [actionSearch, setActionSearch] = useState("")
    const [selectedActionCode, setSelectedActionCode] = useState<DocumentContentActions>(DOCUMENT_CONTENT_ACTIONS[0])
    const [editMode, setEditMode] = useState<EditMode>("single")
    const [editModeAnchor, setEditModeAnchor] = useState<HTMLElement | null>(null)
    const [selectedStatus, setSelectedStatus] = useState<DocumentStatus | null>(null)
    const [selectedSubjectType, setSelectedSubjectType] = useState<SubjectType | null>(null)
    const [pendingCellKeys, setPendingCellKeys] = useState<Set<string>>(new Set())
    const [batchDraft, setBatchDraft] = useState<Record<string, DraftRuleChange>>({})
    const saveInProgress = useRef(false)
    const loadRequestId = useRef(0)

    const isSaving = pendingCellKeys.size > 0
    const isBusy = isLoading || isSaving
    const canEdit = hasLoaded && !isBusy
    const hasAnyBatchChanges = Object.keys(batchDraft).length > 0
    const isContextLocked = isBusy || hasAnyBatchChanges
    const batchChangesCount = Object.keys(batchDraft).length
    const editModeLabel = EDIT_MODES.find(mode => mode.value === editMode)!.label

    useLayoutEffect(() => {
        const panel = panelRef.current
        if (!panel) return

        let frameId = 0
        const viewport = window.visualViewport

        const measure = () => {
            const top = Math.max(0, panel.getBoundingClientRect().top)
            const viewportBottom = viewport
                ? viewport.offsetTop + viewport.height
                : window.innerHeight
            const availableHeight = Math.max(0, Math.floor(viewportBottom - top - PANEL_BOTTOM_GAP))

            setPanelHeight(current => current === availableHeight ? current : availableHeight)
        }

        const scheduleMeasure = () => {
            window.cancelAnimationFrame(frameId)
            frameId = window.requestAnimationFrame(measure)
        }

        measure()
        const observer = new ResizeObserver(scheduleMeasure)
        for (let parent = panel.parentElement; parent; parent = parent.parentElement) {
            observer.observe(parent)
        }

        window.addEventListener("resize", scheduleMeasure)
        viewport?.addEventListener("resize", scheduleMeasure)

        return () => {
            window.cancelAnimationFrame(frameId)
            observer.disconnect()
            window.removeEventListener("resize", scheduleMeasure)
            viewport?.removeEventListener("resize", scheduleMeasure)
        }
    }, [])

    const loadRules = useCallback(async () => {
        const requestId = ++loadRequestId.current
        setIsLoading(true)
        setLoadError(null)

        try {
            const result = await dispatch(loadDocumentContentActionRules())
            if (requestId !== loadRequestId.current) return
            setRules(unwrapResult(result))
            setHasLoaded(true)
        } catch {
            if (requestId !== loadRequestId.current) return
            setLoadError("Nie udało się pobrać reguł akcji dokumentu.")
            setRules([])
            setHasLoaded(false)
        } finally {
            if (requestId !== loadRequestId.current) return
            setIsLoading(false)
        }
    }, [dispatch])

    useEffect(() => {
        void loadRules()

        return () => {
            ++loadRequestId.current
        }
    }, [loadRules])

    const visibleActionCodes = useMemo(() => {
        const query = actionSearch.trim().toLowerCase()
        if (!query) {
            return DOCUMENT_CONTENT_ACTIONS
        }

        return DOCUMENT_CONTENT_ACTIONS.filter(actionCode =>
            actionCode.toLowerCase().includes(query)
        )
    }, [actionSearch])

    useEffect(() => {
        if (!visibleActionCodes.includes(selectedActionCode)) {
            setSelectedActionCode(visibleActionCodes[0] ?? DOCUMENT_CONTENT_ACTIONS[0])
        }
    }, [selectedActionCode, visibleActionCodes])

    const matrix = useMemo(() => {
        const cellSet = new Set<string>()

        rules.forEach(rule => {
            const subjectType = normalizeSubjectType(rule.subjectType)
            const actionCode = normalizeActionCode(rule.actionCode)

            if (!subjectType || actionCode !== selectedActionCode) {
                return
            }

            cellSet.add(getCellKey(rule.documentStatus, subjectType))
        })

        return cellSet
    }, [rules, selectedActionCode])

    const selectedCellKey = selectedStatus !== null && selectedSubjectType !== null
        ? getCellKey(selectedStatus, selectedSubjectType)
        : null

    const isSelectedCellEnabled = selectedCellKey !== null
        ? batchDraft[selectedCellKey]?.enabled ?? matrix.has(selectedCellKey)
        : false

    const handleChangeSelectedAction = (nextActionCode: DocumentContentActions) => {
        if (isContextLocked || saveInProgress.current || nextActionCode === selectedActionCode) return

        setSelectedActionCode(nextActionCode)
        setSelectedStatus(null)
        setSelectedSubjectType(null)
        setSaveError(null)
    }

    const handleEditModeChange = (nextMode: EditMode) => {
        setEditModeAnchor(null)
        if (isContextLocked || saveInProgress.current || nextMode === editMode) return

        setEditMode(nextMode)
        setSelectedStatus(nextMode === "row" ? DOCUMENT_STATUSES[0] ?? null : null)
        setSelectedSubjectType(nextMode === "column"
            ? SYS_DOCUMENT_CONTENT_ACTION_RULE_SUBJECT_TYPES[0] ?? null
            : null)
        setSaveError(null)
    }

    const handleCellClick = (documentStatus: DocumentStatus, subjectType: SubjectType) => {
        if (!canEdit || saveInProgress.current) return
        if (editMode === "column" && selectedSubjectType !== subjectType) return
        if (editMode === "row" && selectedStatus !== documentStatus) return

        if (editMode === "single") {
            setSelectedStatus(documentStatus)
            setSelectedSubjectType(subjectType)
            return
        }

        const cellKey = getCellKey(documentStatus, subjectType)
        const originalEnabled = matrix.has(cellKey)

        setBatchDraft(current => {
            const next = { ...current }
            const currentEnabled = next[cellKey]?.enabled ?? originalEnabled
            const nextEnabled = !currentEnabled

            if (nextEnabled === originalEnabled) {
                delete next[cellKey]
            } else {
                next[cellKey] = {
                    documentStatus,
                    subjectType,
                    enabled: nextEnabled,
                }
            }

            return next
        })
    }

    const handleCancelChanges = () => {
        if (isBusy || saveInProgress.current) return
        setBatchDraft({})
        setSaveError(null)
    }

    const saveChanges = async (changes: DraftRuleChange[], fromBatch: boolean) => {
        if (!canEdit || saveInProgress.current || changes.length === 0) return

        saveInProgress.current = true
        setSaveError(null)
        setPendingCellKeys(new Set(changes.map(change => getCellKey(change.documentStatus, change.subjectType))))

        try {
            const results = await Promise.allSettled(changes.map(async change => {
                const result = await dispatch(upsertSysDocumentContentActionRule({
                    dto: {
                        documentStatus: change.documentStatus,
                        subjectType: change.subjectType,
                        actionCode: selectedActionCode,
                    },
                    enabled: change.enabled,
                }))

                unwrapResult(result)
            }))

            if (fromBatch) {
                setBatchDraft(current => {
                    const next = { ...current }
                    results.forEach((result, index) => {
                        if (result.status === "fulfilled") {
                            const change = changes[index]
                            delete next[getCellKey(change.documentStatus, change.subjectType)]
                        }
                    })
                    return next
                })
            }

            const failedCount = results.filter(result => result.status === "rejected").length
            if (failedCount > 0) {
                setSaveError(fromBatch
                    ? `Nie zapisano ${failedCount} z ${changes.length} zmian. Możesz ponowić zapis.`
                    : "Nie potwierdzono zapisu reguły. Sprawdź stan i spróbuj ponownie.")
            }

            await loadRules()
        } finally {
            saveInProgress.current = false
            setPendingCellKeys(new Set())
        }
    }

    const handleSave = () => {
        if (editMode !== "single") {
            void saveChanges(Object.values(batchDraft), true)
            return
        }

        if (selectedStatus === null || selectedSubjectType === null) return

        void saveChanges([{
            documentStatus: selectedStatus,
            subjectType: selectedSubjectType,
            enabled: !isSelectedCellEnabled,
        }], false)
    }

    const handleRefresh = () => {
        if (isBusy || saveInProgress.current || (hasAnyBatchChanges && hasLoaded)) return
        void loadRules()
    }

    const selectionDescription = editMode === "column"
        ? selectedSubjectType !== null
            ? `Kolumna: ${subjectTypeLabel(selectedSubjectType)}`
            : "Nie wybrano kolumny"
        : editMode === "row"
            ? selectedStatus !== null
                ? `Status: ${selectedStatus}`
                : "Nie wybrano statusu"
            : selectedStatus !== null && selectedSubjectType !== null
                ? `${selectedStatus} / ${subjectTypeLabel(selectedSubjectType)}`
                : "Nie wybrano reguły"

    return (
        <Paper
            ref={panelRef}
            variant="outlined"
            sx={{
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                height: panelHeight ?? "100%",
                minHeight: 0,
                minWidth: 0,
                overflow: "hidden",
            }}
        >
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", md: "center" }}
                sx={{ mb: 1.5, flexShrink: 0 }}
            >
                <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={isBusy || (hasAnyBatchChanges && hasLoaded)}
                    sx={{ textTransform: "none" }}
                >
                    Odśwież
                </Button>

                <TextField
                    size="small"
                    type="search"
                    label="Szukaj akcji"
                    value={actionSearch}
                    onChange={event => setActionSearch(event.target.value)}
                    disabled={isContextLocked}
                    sx={{ flex: "1 1 220px", minWidth: 0 }}
                />

                <FormControl size="small" sx={{ flex: "1 1 280px", minWidth: 0 }}>
                    <InputLabel id={actionLabelId}>Akcja</InputLabel>
                    <Select
                        labelId={actionLabelId}
                        value={selectedActionCode}
                        label="Akcja"
                        disabled={isContextLocked}
                        onChange={event => handleChangeSelectedAction(event.target.value as DocumentContentActions)}
                    >
                        {visibleActionCodes.map(actionCode => (
                            <MenuItem key={actionCode} value={actionCode}>
                                {actionCode}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Button
                    id={modeButtonId}
                    variant="outlined"
                    endIcon={<ExpandMoreIcon />}
                    aria-controls={editModeAnchor ? modeMenuId : undefined}
                    aria-haspopup="menu"
                    aria-expanded={Boolean(editModeAnchor)}
                    disabled={isContextLocked}
                    onClick={event => setEditModeAnchor(event.currentTarget)}
                    sx={theme => ({
                        minHeight: 40,
                        textTransform: "none",
                        bgcolor: editMode !== "single"
                            ? alpha(theme.palette.primary.main, 0.08)
                            : undefined,
                    })}
                >
                    Edycja: {editModeLabel}
                </Button>

                <Menu
                    id={modeMenuId}
                    anchorEl={editModeAnchor}
                    open={Boolean(editModeAnchor)}
                    onClose={() => setEditModeAnchor(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    {EDIT_MODES.map(mode => (
                        <MenuItem
                            key={mode.value}
                            selected={editMode === mode.value}
                            role="menuitemradio"
                            aria-checked={editMode === mode.value}
                            onClick={() => handleEditModeChange(mode.value)}
                            sx={{ gap: 1.5, py: 1 }}
                        >
                            <Box sx={{ width: 20, flexShrink: 0, display: "flex", color: "primary.main" }}>
                                {editMode === mode.value && <CheckIcon fontSize="small" />}
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {mode.label}
                            </Typography>
                        </MenuItem>
                    ))}
                </Menu>
            </Stack>

            {loadError && <Alert severity="error" sx={{ mb: 1.5 }}>{loadError}</Alert>}
            {saveError && <Alert severity="error" sx={{ mb: 1.5 }}>{saveError}</Alert>}

            {visibleActionCodes.length === 0 ? (
                <Box sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                        Brak akcji pasujących do wyszukiwania.
                    </Typography>
                </Box>
            ) : (
                <TableContainer sx={{ flex: "1 1 0%", minHeight: 0, minWidth: 0, overflow: "auto" }}>
                    <Table stickyHeader size="small" aria-label="Statusy i role dla wybranej akcji">
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        minWidth: 220,
                                        fontWeight: 700,
                                        bgcolor: "grey.200",
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 4,
                                    }}
                                >
                                    Status dokumentu
                                </TableCell>
                                {SYS_DOCUMENT_CONTENT_ACTION_RULE_SUBJECT_TYPES.map(subjectType => {
                                    const columnSelected = selectedSubjectType === subjectType
                                        && (editMode === "column" || (editMode === "single" && selectedCellKey !== null))
                                    return (
                                        <TableCell key={subjectType} align="center" sx={{ minWidth: 140, p: 0, bgcolor: "grey.200" }}>
                                            <ButtonBase
                                                disabled={editMode !== "column" || !canEdit || hasAnyBatchChanges}
                                                onClick={() => setSelectedSubjectType(subjectType)}
                                                aria-label={`Wybierz kolumnę: ${subjectTypeLabel(subjectType)}`}
                                                aria-pressed={columnSelected}
                                                sx={theme => ({
                                                    width: "100%",
                                                    minHeight: 44,
                                                    px: 1.5,
                                                    py: 1,
                                                    fontWeight: 600,
                                                    color: columnSelected ? "success.main" : "text.primary",
                                                    bgcolor: columnSelected
                                                        ? alpha(theme.palette.success.main, SELECTION_OPACITY.axis)
                                                        : theme.palette.grey[200],
                                                    boxShadow: columnSelected
                                                        ? `inset 2px 0 0 ${theme.palette.success.main}, inset -2px 0 0 ${theme.palette.success.main}, inset 0 2px 0 ${theme.palette.success.main}`
                                                        : undefined,
                                                })}
                                            >
                                                {subjectTypeLabel(subjectType)}
                                            </ButtonBase>
                                        </TableCell>
                                    )
                                })}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {DOCUMENT_STATUSES.map((status, statusIndex) => {
                                const rowSelected = selectedStatus === status
                                    && (editMode === "row" || (editMode === "single" && selectedCellKey !== null))
                                const isLastRow = statusIndex === DOCUMENT_STATUSES.length - 1

                                return (
                                    <TableRow key={status}>
                                        <TableCell
                                            sx={{
                                                p: 0,
                                                bgcolor: "grey.100",
                                                position: "sticky",
                                                left: 0,
                                                zIndex: 1,
                                            }}
                                        >
                                            <ButtonBase
                                                disabled={editMode !== "row" || !canEdit || hasAnyBatchChanges}
                                                onClick={() => setSelectedStatus(status)}
                                                aria-label={`Wybierz status: ${status}`}
                                                aria-pressed={rowSelected}
                                                sx={theme => ({
                                                    width: "100%",
                                                    minHeight: 44,
                                                    px: 1.5,
                                                    py: 0.75,
                                                    justifyContent: "flex-start",
                                                    textAlign: "left",
                                                    color: rowSelected ? "success.main" : "text.primary",
                                                    bgcolor: rowSelected
                                                        ? alpha(theme.palette.success.main, SELECTION_OPACITY.axis)
                                                        : theme.palette.grey[100],
                                                    boxShadow: rowSelected
                                                        ? `inset 2px 0 0 ${theme.palette.success.main}, inset 0 2px 0 ${theme.palette.success.main}, inset 0 -2px 0 ${theme.palette.success.main}`
                                                        : undefined,
                                                })}
                                            >
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <DocumentStatusLabel status={status} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        ({status})
                                                    </Typography>
                                                </Stack>
                                            </ButtonBase>
                                        </TableCell>

                                        {SYS_DOCUMENT_CONTENT_ACTION_RULE_SUBJECT_TYPES.map((subjectType, subjectIndex) => {
                                            const cellKey = getCellKey(status, subjectType)
                                            const draft = batchDraft[cellKey]
                                            const isEnabled = draft?.enabled ?? matrix.has(cellKey)
                                            const isPending = pendingCellKeys.has(cellKey)
                                            const columnSelected = selectedSubjectType === subjectType
                                                && (editMode === "column" || (editMode === "single" && selectedCellKey !== null))
                                            const cellSelected = editMode === "single" && selectedCellKey === cellKey
                                            const isLastColumn = subjectIndex === SYS_DOCUMENT_CONTENT_ACTION_RULE_SUBJECT_TYPES.length - 1
                                            const outsideScope = (editMode === "column" && !columnSelected)
                                                || (editMode === "row" && !rowSelected)

                                            return (
                                                <TableCell key={`${status}-${subjectType}`} align="center" sx={{ p: 0 }}>
                                                    <ButtonBase
                                                        disabled={!canEdit || outsideScope}
                                                        onClick={() => handleCellClick(status, subjectType)}
                                                        aria-label={`${status}, ${subjectTypeLabel(subjectType)}: ${isEnabled ? "włączona" : "wyłączona"}${draft ? ", niezapisana zmiana" : ""}`}
                                                        aria-pressed={editMode === "single" ? cellSelected : undefined}
                                                        aria-busy={isPending}
                                                        sx={theme => ({
                                                            position: "relative",
                                                            width: "100%",
                                                            minHeight: 44,
                                                            py: 1,
                                                            color: isEnabled ? "success.main" : "text.disabled",
                                                            bgcolor: cellSelected || rowSelected || columnSelected
                                                                ? alpha(theme.palette.success.main, cellSelected ? SELECTION_OPACITY.cell : SELECTION_OPACITY.axis)
                                                                : undefined,
                                                            boxShadow: cellSelected
                                                                ? `inset 0 0 0 3px ${theme.palette.success.main}`
                                                                : [
                                                                    rowSelected ? `inset 0 2px 0 ${theme.palette.success.main}` : null,
                                                                    rowSelected || (columnSelected && isLastRow)
                                                                        ? `inset 0 -2px 0 ${theme.palette.success.main}` : null,
                                                                    columnSelected ? `inset 2px 0 0 ${theme.palette.success.main}` : null,
                                                                    columnSelected || (rowSelected && isLastColumn)
                                                                        ? `inset -2px 0 0 ${theme.palette.success.main}` : null,
                                                                ].filter(Boolean).join(", ") || undefined,
                                                            "&:hover": {
                                                                bgcolor: alpha(theme.palette.success.main,
                                                                    cellSelected ? 0.24 : rowSelected || columnSelected ? 0.14 : 0.04),
                                                            },
                                                        })}
                                                    >
                                                        {isPending
                                                            ? <CircularProgress size={18} />
                                                            : isEnabled
                                                                ? <CheckIcon fontSize="small" />
                                                                : <Typography component="span" variant="body2">—</Typography>}
                                                        {draft && (
                                                            <Box
                                                                title="Niezapisana zmiana"
                                                                aria-hidden="true"
                                                                sx={{
                                                                    position: "absolute",
                                                                    top: 7,
                                                                    right: 9,
                                                                    width: 7,
                                                                    height: 7,
                                                                    borderRadius: "50%",
                                                                    bgcolor: "warning.main",
                                                                }}
                                                            />
                                                        )}
                                                    </ButtonBase>
                                                </TableCell>
                                            )
                                        })}
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Stack
                direction="row"
                spacing={1}
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 1.5, flexShrink: 0 }}
            >
                <Typography variant="body2" color="text.secondary" noWrap title={selectionDescription}>
                    {selectionDescription}
                </Typography>

                <Stack direction="row" spacing={1}>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={handleCancelChanges}
                    disabled={editMode === "single" || !hasAnyBatchChanges || isBusy}
                    sx={{ textTransform: "none" }}
                >
                    Odrzuć zmiany
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    onClick={handleSave}
                    disabled={!canEdit || (editMode === "single" ? selectedCellKey === null : !hasAnyBatchChanges)}
                    startIcon={isSaving
                        ? <CircularProgress size={16} color="inherit" />
                        : editMode === "single"
                            ? isSelectedCellEnabled ? <LockOutlinedIcon /> : <LockOpenOutlinedIcon />
                            : undefined}
                    color={
                        editMode !== "single"
                            ? "primary"
                            : isSelectedCellEnabled
                                ? "error"
                                : "success"
                    }
                    sx={{ textTransform: "none" }}
                >
                    {isSaving
                        ? "Zapisywanie..."
                        : editMode !== "single"
                            ? `Zapisz zmiany (${batchChangesCount})`
                            : isSelectedCellEnabled
                                ? "Wyłącz regułę"
                                : "Włącz regułę"}
                </Button>
                </Stack>
            </Stack>
        </Paper>
    )
}

export default SysDocumentContentActionByActionView
